import { Trans, useLingui } from '@lingui/react/macro';
import { useForm } from 'react-hook-form';

import { Checkbox, Field, Label } from '@remarkable/ark-web';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import { useIntegrationTermsOfService } from 'src/api/queries';
import { Button, Form, NotificationBox, Sheet, Spinner } from 'src/components';
import { LinkExternal } from 'src/components/LinkExternal';
import { SUPPORT_URL } from 'src/utils/urls/supportUrls';
import { Description } from './Description';

type Inputs = {
  accept: boolean | undefined;
};

export const TermsOfServiceForm = () => {
  const { t } = useLingui();
  const form = useForm<Inputs>();
  const tos = useIntegrationTermsOfService();
  const checkedTOS = form.watch('accept');

  if (tos.status.isPending) return <Spinner />;
  if (tos.status.isError)
    return (
      <NotificationBox error={tos.status.error}>
        <Trans>
          We were not able to get status for terms of service. Please reload the
          page or contact support if the problem persists.
        </Trans>
      </NotificationBox>
    );

  if (tos.hasAccepted) return null;

  return (
    <Sheet className="mb-40">
      <Description />
      <Form onSubmit={() => tos.accept.mutate()} {...form}>
        <p className="body-md">
          <Trans>
            We encourage you to read the full terms of service below.
          </Trans>
        </p>

        <Field variant="horizontal" className="mt-24">
          <Checkbox
            required
            id="accept-tos-checkbox"
            data-cy="accept-tos-checkbox"
            {...form.register('accept', {
              required: true,
            })}
          />
          <Label htmlFor="accept-tos-checkbox">
            <Trans comment="Checkbox label for accepting the integrations terms of service">
              I accept the{' '}
              <LinkExternal
                data-cy="tos-integrations-link"
                to={SUPPORT_URL.TERMS_AND_CONDITIONS_INTEGRATIONS}
                inline
                componentLocation={
                  ComponentLocations.INTEGRATIONS.TERMS_OF_SERVICE
                }
              >
                Terms of Service
              </LinkExternal>
            </Trans>
          </Label>
        </Field>

        <Button
          type="submit"
          variant="primary"
          disabled={tos.accept.isPending || !checkedTOS}
          loading={tos.accept.isPending}
          className="mt-24"
          data-cy="accept-tos-confirm-button"
        >
          <Trans comment="Button label to confirm the integrations terms of service">
            Confirm
          </Trans>
        </Button>
        {tos.accept.isError && (
          <NotificationBox
            error={tos.accept.error}
            title={t`Something went wrong`}
            className="my-16"
          >
            <Trans>
              We were not able to accept the terms of service. Please try again
              later or contact support if the problem persists.
            </Trans>
          </NotificationBox>
        )}
      </Form>
    </Sheet>
  );
};
